import { buildQueryString } from '../../Shared/utils/url-utils';
import {
	WebGivingMerchant,
	CustomFieldEditModel,
	CustomFieldType,
	ICustomFieldViewModel,
	DropDownCustomFieldOptionViewModel,
	FundCustomFieldOptionViewModel,
} from '../webgiving-generated';
import { IWebGivingPaymentState } from '../webgiving-payment-state';


export type SupportedKioskLinkDetails = {
	supported: true;
	merchantHandle: string;
	paymentAmount: number;
	referenceFields: (string | null)[];
	urlParameters: string | null;
};

export type KioskLinkDetails = {
	supported: false;
} | SupportedKioskLinkDetails;

export function buildLinkDetails(merchant: WebGivingMerchant, paymentState: IWebGivingPaymentState): KioskLinkDetails {
	if (!merchant.IsHppEnabled) {
		return { supported: false };
	}

	if (merchant.PayersMustBeVerified) {
		return { supported: false };
	}

	const referenceFieldValues = merchant.CustomFields
		.map(f => getReferenceFieldValue(f, paymentState));

	return {
		supported: true,
		merchantHandle: merchant.Handle,
		paymentAmount: paymentState.amount,
		referenceFields: referenceFieldValues,
		urlParameters: paymentState.urlParameters,
	};
}

export function buildKioskLink(reason: string, details: KioskLinkDetails) {
	if (!details.supported) {
		throw new Error('Cannot build a kiosk link because the merchant requires all the payers to be verified');
	}

	return generateKioskLink(reason, details);
}

function getReferenceFieldValue(fieldDefinition: ICustomFieldViewModel, paymentState: IWebGivingPaymentState): string | null {
	const field = getMatchingReferenceField(paymentState, fieldDefinition.Key);
	if (field === null) {
		return null;
	}

	if (fieldDefinition.Type === CustomFieldType.DropDown || fieldDefinition.Type === CustomFieldType.Fund) {
		const selectedValue = field.Value;
		const options: (DropDownCustomFieldOptionViewModel | FundCustomFieldOptionViewModel)[] = fieldDefinition.Options;
		const matchingOptions = options.filter(opt => opt.Value === selectedValue);
		if (matchingOptions.length > 0) {
			return matchingOptions[0].Label;
		}
		return null;
	}
	return field.Value || null;
}

function getMatchingReferenceField(paymentState: IWebGivingPaymentState, key: string): CustomFieldEditModel | null {
	const matchingValues = paymentState.referenceFieldValues.filter(f => f.Key === key);
	if (matchingValues.length > 0) {
		return matchingValues[0];
	}
	return null;
}

function generateKioskLink(reasonForGoingToKiosk: string, details: SupportedKioskLinkDetails) {
	const { merchantHandle } = details;
	const urlParameters = buildUrlParameters(reasonForGoingToKiosk, details);
	const queryString = buildQueryString(urlParameters);
	return `/kiosk/${merchantHandle}?${queryString}`;
}

export function getUrlParameter(parameters: string | null, key: string) {
	var value = '';
	if (parameters) {
		parameters = parameters.toLowerCase();
		parameters.substr(1).split('&').forEach(item => {
			var s = item.split('='),
				k = s[0],
				v = s[1];
			if (k === key && v) {
				value = v;
				return;
			}
		});
	}
	return value;
}

function buildUrlParameters(reasonForGoingToKiosk: string, details: SupportedKioskLinkDetails): { [key: string]: string } {

	const { paymentAmount, referenceFields, urlParameters } = details;

	// 'src' we use by convention solution-wide for tracking redirects.
	// See https://care.echurchgiving.com/hc/en-us/articles/115001349931 for mapping of
	// values to other URL parameter codes.
	const queryParameters: any = {
		'src': reasonForGoingToKiosk,
	};

	if (paymentAmount) {
		queryParameters['a'] = paymentAmount;
	}

	const sr = getUrlParameter(urlParameters, 'sr');
	if (sr) {
		queryParameters['sr'] = sr;
	}

	const ru = getUrlParameter(urlParameters, 'ru');
	if (ru) {
		queryParameters['ru'] = ru;
	}

	if (referenceFields) {
		for (let i = 0; i < referenceFields.length; i++) {
			const field = referenceFields[i];
			if (field) {
				queryParameters[`f[${i}]`] = field;
			}
		}
	}

	return queryParameters;
}
